<template>
  <div class="bg-body rounded p-5 pb-3 mb-5">
    <div class="d-flex align-items-center flex-wrap gap-3 mb-3">
      <h4 class="align-items-start flex-column fw-bold text-gray-900 m-0 ms-2">
        {{ this.$route.meta.title || 'Ценовые кампании' }}
      </h4>
      <div class="custom-select dynamic ms-auto" style="min-width: 200px;">
        <v-select
          v-model="statusFilter"
          input-id="priceCampaign.statusFilter"
          :options="statusFilterOptions"
          :reduce="(item) => item.code"
          label="label"
          placeholder="Статус..."
          class="bg-gray-100 form rounded"
          @update:modelValue="onStatusFilterChange"
        />
      </div>
      <div @click="openItemModal(null)" class="btn btn-sm btn-success">Создать кампанию</div>
    </div>
  </div>
  <div v-if="!loaded" class="card card-body p-0 border-top border-top-1 border-gray-200">
    <div class="table-responsive custom-table">
      <table class="table table-hover table-rounded table-striped table-row-dashed fs-7 table-row-gray-300 gy-5 custom-table align-middle mb-0">
        <thead>
        <tr class="fw-bold fs-8 text-gray-400">
          <th>ID</th>
          <th>Цена</th>
          <th>Начало</th>
          <th>Окончание</th>
          <th>Статус</th>
          <th class="text-end">Действие</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, key) in campaigns" :key="key">
          <td>{{ item.id }}</td>
          <td>{{ item.price }}</td>
          <td>{{ formatDateTime(item.startDateTime) }}</td>
          <td>{{ formatDateTime(item.endDateTime) }}</td>
          <td>{{ statusLabel(item.status) }}</td>
          <td class="text-end">
            <div class="d-flex flex-wrap justify-content-end gap-2 campaign-actions">
              <button
                v-if="canEdit(item)"
                type="button"
                class="btn btn-sm btn-light-primary"
                @click="openItemModal(item.id)"
              >
                Редактировать
              </button>
              <button
                v-if="canDelete(item)"
                type="button"
                class="btn btn-sm btn-danger"
                @click="deleteCampaign(item.id)"
              >
                Удалить
              </button>
              <span v-if="item.status === 'ACTIVE'" class="text-muted fs-8 align-self-center">Активная кампания</span>
            </div>
          </td>
        </tr>
        <tr v-if="!campaigns || campaigns.length === 0">
          <td colspan="6" class="text-center py-8 text-gray-500">Список пуст</td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="text-center border-top py-3">
      <div class="btn-group">
        <button class="btn btn-sm btn-light" :disabled="page === 0" @click="goToPage(page - 1)">
          <i class="ki-outline ki-arrow-left"></i>
        </button>
        <button
          v-for="(p, i) in visiblePages"
          :key="i"
          class="btn btn-sm btn-light"
          :class="{
            'text-gray-600': p !== '...' && page !== p,
            'text-success fw-bold': p !== '...' && page === p,
            'btn-disabled text-muted': p === '...'
          }"
          :disabled="p === '...'"
          @click="p !== '...' && goToPage(p)"
        >
          {{ p === '...' ? '...' : p + 1 }}
        </button>
        <button class="btn btn-sm btn-light" :disabled="page === totalPages - 1 || totalPages === 0" @click="goToPage(page + 1)">
          <i class="ki-outline ki-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
  <div v-else class="card rounded">
    <loader />
  </div>
  <price-campaign-modal
    v-if="showModal"
    :item-data="selectedItem"
    @closeModal="closeModal"
  />
</template>

<script>
import PriceCampaignModal from '@/views/EnergoGoClub/PriceCampaign/PriceCampaignModal'
import Loader from '@/components/Loader.vue'
import vSelect from 'vue-select'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'

const API_BASE = '/api/admin/v1/price-campaigns'

const STATUS_LABELS = {
  PENDING: 'Ожидает',
  ACTIVE: 'Активна',
  COMPLETED: 'Завершена',
}

export default {
  name: 'PriceCampaign',
  components: { PriceCampaignModal, Loader, vSelect },
  data() {
    return {
      loaded: false,
      campaigns: [],
      statuses: [],
      statusFilter: null,
      page: 0,
      size: 20,
      totalPages: 1,
      showModal: false,
      selectedItem: null,
    };
  },
  computed: {
    statusFilterOptions() {
      return [
        { code: null, label: 'Все' },
        ...this.statuses.map((code) => ({
          code,
          label: STATUS_LABELS[code] || code,
        })),
      ];
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        for (let i = 0; i < total; i += 1) pages.push(i);
      } else {
        pages.push(0);
        if (current > 3) pages.push('...');
        const start = Math.max(1, current - 1);
        const end = Math.min(total - 2, current + 1);
        for (let i = start; i <= end; i += 1) pages.push(i);
        if (current < total - 4) pages.push('...');
        pages.push(total - 1);
      }
      return pages;
    },
  },
  mounted() {
    this.fetchStatuses();
    this.getCampaigns();
  },
  methods: {
    getErrorMessage(err) {
      return err?.response?.data?.message
        ?? err?.response?.data?.errorDescription
        ?? err.message;
    },
    fetchStatuses() {
      axios.get(`${API_BASE}/statuses`)
        .then((res) => {
          this.statuses = Array.isArray(res?.data) ? res.data : [];
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
          this.statuses = [];
        });
    },
    getCampaigns() {
      this.loaded = true;
      const params = {
        page: this.page,
        size: this.size,
        sort: 'createdAt,desc',
      };
      if (this.statusFilter) {
        params.status = this.statusFilter;
      }
      axios.get(API_BASE, { params })
        .then((res) => {
          const payload = res?.data;
          const content = Array.isArray(payload?.content) ? payload.content : [];
          this.campaigns = content;
          this.page = payload?.number ?? payload?.pageable?.pageNumber ?? this.page;
          this.totalPages = Math.max(1, Number(payload?.totalPages ?? 1));
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
          this.campaigns = [];
          this.totalPages = 1;
        })
        .finally(() => {
          this.loaded = false;
        });
    },
    onStatusFilterChange() {
      this.page = 0;
      this.getCampaigns();
    },
    openItemModal(id) {
      if (!id) {
        this.selectedItem = null;
        this.showModalWithBootstrap();
        return;
      }
      this.loaded = true;
      axios.get(`${API_BASE}/${id}`)
        .then((res) => {
          this.selectedItem = res?.data || null;
          this.showModalWithBootstrap();
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.loaded = false;
        });
    },
    deleteCampaign(id) {
      if (!id) return;
      if (!window.confirm('Удалить ценовую кампанию?')) return;
      axios.delete(`${API_BASE}/${id}`)
        .then(() => {
          notify({
            duration: 5000,
            type: 'success',
            speed: 1000,
            title: 'Успешно',
            text: 'Кампания удалена',
          });
          this.getCampaigns();
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
        });
    },
    showModalWithBootstrap() {
      this.showModal = true;
      setTimeout(() => {
        const modalEl = document.getElementById('priceCampaignModal');
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }, 10);
    },
    closeModal(isUpdate) {
      this.showModal = false;
      this.selectedItem = null;
      if (isUpdate) this.getCampaigns();
    },
    goToPage(newPage) {
      if (newPage >= 0 && newPage < this.totalPages) {
        this.page = newPage;
        this.getCampaigns();
      }
    },
    formatDateTime(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return String(iso);
      return date.toLocaleString('ru-RU', { timeZone: 'Asia/Tashkent' });
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || '';
    },
    canEdit(item) {
      return item?.status === 'PENDING';
    },
    canDelete(item) {
      return item?.status === 'PENDING' || item?.status === 'COMPLETED';
    },
  },
}
</script>

<style scoped>
.campaign-actions {
  min-width: 160px;
}
</style>
